import { useState, type ReactNode } from 'react';
import { useTabParam } from '@/hooks/useTabParam';
import { roleLabels } from '@/services/roleLabels';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users, Building2, Plus, Pencil, Trash2, Archive, RotateCcw, Loader2, Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthStore } from '@/stores/authStore';
import { useMembers, useMembersPage } from '@/hooks/queries/useAdmin';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/vf/PageHeader';
import {
  useInviteMember, useChangeMemberRole, useRemoveMember, useSetMemberCustomerIds,
} from '@/hooks/mutations/useAdminMutations';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  listCustomers, createCustomer, updateCustomer, archiveCustomer, reactivateCustomer, migrateFromTenant, backfillOrganisationId,
  customerHubspotAuthorizeUrl, disconnectCustomerHubspot,
  customerGoogleAuthorizeUrl, disconnectCustomerGoogle,
  type Customer,
} from '@/api/endpoints/customers';
import { toast } from 'sonner';
import { HUBSPOT_UI_ENABLED } from '@/lib/hubspotUi';
import { useEffect } from 'react';
import { api } from '@/api/api-client';
import { showErrorToast } from '@/lib/errorToast';
import type { Role } from '@revcall/shared';
import { oauthReasonMessage } from '@/lib/oauthReason';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import { listMembersJournal } from '@/api/endpoints/membersJournal';

/**
 * Unified team management screen — supersedes legacy AdminCallers + AdminClients.
 *
 * 3 tabs scoped to the active Organisation:
 *   1. Membres        → list, invite, change role, remove. (v2 Memberships)
 *   2. Customers      → CRUD of B2B clients of the tenant (ADR-024).
 *   3. Assignations   → per-Caller, pick which Customers they can dial for.
 *
 * W4 (B2-team): `?tab=` deep link; the « Clients » column shows each
 * member's customers as pills (callers included: who calls for whom); a role
 * change runs at once with an « Annuler » toast (DES-13), a removal asks
 * through ConfirmDialog; seats used; the role journal (audit log).
 *
 * W6 (DUP-3): the only Team page. A member opens it at /team in the dialer;
 * the super admin opens it from an organisation row (which makes that
 * organisation active) and gets it in the back-office shell, with the page's
 * <h1> and the p-6 margin the back office gives every page (QW-h8). The
 * members table is paged by the server, with the total (SR1-1.2).
 *
 * Authorization: any member of the tenant can VIEW. Mutations (invite,
 * change role, remove, customers CRUD, assignments) require tenant_admin
 * (or super_admin) — enforced both in this UI (canEdit gate) and on the
 * backend (route handlers reject non-admin writes).
 */


const ROLE_VARIANTS: Record<Role, 'default' | 'secondary' | 'outline'> = {
  tenant_admin: 'default',
  caller: 'secondary',
  viewer: 'outline',
};

const INVITABLE_ROLES: Role[] = ['tenant_admin', 'caller', 'viewer'];

const TEAM_TABS = ['members', 'customers', 'assignments'] as const;
type TeamTab = (typeof TEAM_TABS)[number];

export default function TeamManagement() {
  const { t } = useTranslation(['settings', 'common']);
  const activeMembership = useAuthStore((s) => s.activeMembership);
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin === true);
  const organisationId = activeMembership?.organisationId;
  // ADR-031 — any member of the tenant can VIEW this page; only tenant_admin
  // (or super_admin) can mutate. The backend mirrors this guard.
  const canEdit = isSuperAdmin || activeMembership?.role === 'tenant_admin';
  // ?tab=customers opens the Clients tab (link from AdminClientDetail).
  const [askedTeamTab, setTeamTab] = useTabParam(TEAM_TABS, 'members');
  // Assignments is an editor tab; a reader asking for it lands on Members.
  const teamTab = !canEdit && askedTeamTab === 'assignments' ? 'members' : askedTeamTab;
  const qc = useQueryClient();

  // ADR-024 phase 2.c — handle the OAuth callback redirect:
  // /TeamManagement?hubspot=connected&customerId=...
  // /TeamManagement?google=connected&customerId=...
  // /TeamManagement?{hubspot,google}=error&reason=...
  // We toast + invalidate the customers query so the connector chip
  // flips to "Connecté" without a manual refresh.
  useEffect(() => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const hub = params.get('hubspot');
    const goog = params.get('google');
    // A fixed code from the callback (AUDIT-INT-10/15), turned into our own
    // sentence: the raw value is never shown.
    const reason = params.get('reason') ? oauthReasonMessage(t, params.get('reason')) : '';
    let dirty = false;
    if (hub === 'connected') { toast.success(t('settings:teamManagement.toasts.hubspotConnected')); dirty = true; }
    if (hub === 'error') { toast.error(t('settings:teamManagement.toasts.hubspotError', { reason: reason || t('settings:teamManagement.toasts.genericError') })); dirty = true; }
    if (goog === 'connected') { toast.success(t('settings:teamManagement.toasts.googleConnected')); dirty = true; }
    if (goog === 'error') { toast.error(t('settings:teamManagement.toasts.googleError', { reason: reason || t('settings:teamManagement.toasts.genericError') })); dirty = true; }
    if (dirty) {
      qc.invalidateQueries({ queryKey: ['customers'] });
      // Clean the URL so a refresh doesn't re-trigger the toast.
      ['hubspot', 'google', 'reason', 'customerId'].forEach((k) => params.delete(k));
      window.history.replaceState({}, '', url.pathname + (params.toString() ? '?' + params.toString() : ''));
    }
  }, [qc]);

  if (!organisationId) {
    // The super admin has no active organisation by default: one way out,
    // the organisations table, whose rows open their team (DES-12, one CTA).
    if (isSuperAdmin) {
      return (
        <AdminTeamFrame title={t('settings:teamManagement.adminTitle')}>
          <div className="flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
            <p className="text-sm">{t('settings:teamManagement.adminNoOrganisation')}</p>
            <Button asChild size="sm"><Link to="/AdminOrganisations">{t('settings:teamManagement.adminPickOrganisation')}</Link></Button>
          </div>
        </AdminTeamFrame>
      );
    }
    return (
      <div className="p-6">
        <Alert>
          <AlertDescription>
            {t('settings:teamManagement.noOrganisation')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ADR-031 — in the dialer (/team) the PageHeader and the padding come from
  // DialerLayout (HEADER_BY_VIEW.team). The super admin sees it in the
  // back-office shell, which gives neither: AdminTeamFrame adds them (QW-h8).
  const body = (
    <div className="space-y-6">
      {!canEdit && (
        <p className="text-xs text-muted-foreground italic">
          {t('settings:teamManagement.readOnly')}
        </p>
      )}

      <Tabs value={teamTab} onValueChange={(v) => setTeamTab(v as TeamTab)} className="w-full">
        <TabsList
          className={`grid w-full max-w-lg ${canEdit ? 'grid-cols-3' : 'grid-cols-2'}`}
        >
          <TabsTrigger value="members" className="gap-2 px-2 sm:px-3">
            <Users className="size-4 hidden sm:block" /> {t('settings:teamManagement.tabs.members')}
          </TabsTrigger>
          <TabsTrigger value="customers" className="gap-2 px-2 sm:px-3">
            <Building2 className="size-4 hidden sm:block" /> {t('settings:teamManagement.tabs.customers')}
          </TabsTrigger>
          {canEdit && (
            <TabsTrigger value="assignments" className="gap-2 px-2 sm:px-3">
              <Link2 className="size-4 hidden sm:block" /> {t('settings:teamManagement.tabs.assignments')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="members" className="mt-6">
          <MembersSection organisationId={organisationId} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
          <CustomersSection canEdit={canEdit} />
        </TabsContent>

        {canEdit && (
          <TabsContent value="assignments" className="mt-6">
            <AssignmentsSection organisationId={organisationId} canEdit={canEdit} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
  if (!isSuperAdmin) return body;
  return (
    <AdminTeamFrame title={t('settings:teamManagement.adminTitle')} subtitle={activeMembership?.organisationName}>
      {body}
    </AdminTeamFrame>
  );
}

/** The back-office frame of the Team page (super admin): margin and the page's <h1>. */
function AdminTeamFrame({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5" data-testid="team-admin-frame">
      <PageHeader as="h1" title={title} subtitle={subtitle} />
      {children}
    </div>
  );
}

const MEMBERS_PAGE_SIZE = 25;

// ─── Members section ─────────────────────────────────────────────────────

function MembersSection({ organisationId, canEdit }: { organisationId: string; canEdit: boolean }) {
  const { t } = useTranslation(['settings', 'common']);
  // SR1-1.2: a server page of members; the count and the seats line read the
  // whole organisation (total, by_role), never the page shown.
  const [page, setPage] = useState(0);
  const membersPage = useMembersPage(organisationId, { limit: MEMBERS_PAGE_SIZE, offset: page * MEMBERS_PAGE_SIZE });
  const members = membersPage.data?.members ?? [];
  const total = membersPage.data?.total ?? 0;
  const isLoading = membersPage.isLoading;
  const pageCount = Math.max(1, Math.ceil(total / MEMBERS_PAGE_SIZE));
  // A removal can empty the last page: step back instead of stranding the user.
  useEffect(() => {
    if (membersPage.data && page > 0 && page >= pageCount) setPage(pageCount - 1);
  }, [membersPage.data, page, pageCount]);
  // ADR-027 — load active Customers once for the viewer-scope picker.
  const { data: customers = [] } = useQuery({
    queryKey: ['customers', { activeOnly: true }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: true, signal }),
    staleTime: 60_000,
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const colCount = canEdit ? 6 : 5;
  const byRole = (r: Role) => membersPage.data?.by_role[r] ?? 0;
  const myMembershipId = useAuthStore((s) => s.activeMembership?.membershipId);
  const adminCount = byRole('tenant_admin');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          {/* REV-W6-F2: without the list the count is unknown, never « 0 membres ». */}
          <p className="text-sm text-muted-foreground" data-testid="team-members-count">
            {membersPage.data ? t('settings:teamManagement.members.count', { count: total }) : !isLoading ? t('common:stat.unavailable') : '\u00a0'}
          </p>
          {membersPage.data && (
            <p className="text-xs text-muted-foreground" data-testid="team-seats">
              {t('settings:teamManagement.seats.summary', { admins: byRole('tenant_admin'), callers: byRole('caller'), viewers: byRole('viewer') })}
              {' · '}{t('settings:teamManagement.seats.unlimited')}
            </p>
          )}
        </div>
        {canEdit && (
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <Plus className="size-4" /> {t('settings:teamManagement.members.invite')}
              </Button>
            </DialogTrigger>
            <InviteMemberDialog
              organisationId={organisationId}
              onClose={() => setInviteOpen(false)}
            />
          </Dialog>
        )}
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('settings:teamManagement.members.columns.name')}</TableHead>
              <TableHead>{t('settings:teamManagement.members.columns.email')}</TableHead>
              <TableHead>{t('settings:teamManagement.members.columns.role')}</TableHead>
              <TableHead>{t('settings:teamManagement.members.columns.accessibleCustomers')}</TableHead>
              <TableHead className="w-[100px]">{t('settings:teamManagement.members.columns.status')}</TableHead>
              {canEdit && <TableHead className="w-[80px] text-right">{t('settings:teamManagement.members.columns.actions')}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={colCount} className="text-center py-8">
                <Loader2 className="size-5 animate-spin mx-auto" />
              </TableCell></TableRow>
            ) : membersPage.isError ? (
              <TableRow><TableCell colSpan={colCount} className="py-8">
                <div role="alert" className="flex flex-col items-center gap-2 text-sm">
                  <p>{t('settings:teamManagement.members.loadFailed')}</p>
                  <Button size="sm" variant="outline" onClick={() => void membersPage.refetch()}>{t('common:actions.retry')}</Button>
                </div>
              </TableCell></TableRow>
            ) : members.length === 0 ? (
              <TableRow><TableCell colSpan={colCount} className="text-center py-8 text-muted-foreground text-sm">
                {t('settings:teamManagement.members.empty')}{canEdit ? ` ${t('settings:teamManagement.members.emptyInviteFirst')}` : ''}
              </TableCell></TableRow>
            ) : (
              members.map((m) => (
                <MemberRow
                  key={m.membership_id}
                  m={m}
                  organisationId={organisationId}
                  customers={customers}
                  canEdit={canEdit}
                  isSelf={!!myMembershipId && m.membership_id === myMembershipId}
                  isLastAdmin={m.role === 'tenant_admin' && adminCount <= 1}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {total > MEMBERS_PAGE_SIZE && (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground" data-testid="team-members-pager">
          <span className="tabular-nums">
            {t('common:dataTable.range', { from: page * MEMBERS_PAGE_SIZE + 1, to: page * MEMBERS_PAGE_SIZE + members.length, total })}
          </span>
          <span className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>{t('common:actions.previous')}</Button>
            <Button size="sm" variant="outline" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>{t('common:actions.next')}</Button>
          </span>
        </div>
      )}

      {canEdit && <RoleJournal organisationId={organisationId} />}
    </div>
  );
}

/** W4 (B2-team): who changed which role, invited or removed whom, and when (audit log). */
function RoleJournal({ organisationId }: { organisationId: string }) {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const ROLE_LABELS = roleLabels(t);
  const [open, setOpen] = useState(false);
  const journal = useQuery({
    // Under the members key (REV-W4-F13): every role change, invitation or
    // removal (they invalidate ['organisation', id, 'members']) refreshes it.
    queryKey: ['organisation', organisationId, 'members', 'journal'],
    queryFn: ({ signal }) => listMembersJournal(organisationId, signal),
    enabled: open,
  });
  const role = (r: Role | null) => (r ? ROLE_LABELS[r] ?? r : '—');
  return (
    <section className="rounded-lg border">
      <button
        type="button"
        className="flex min-h-11 w-full items-center justify-between px-4 py-2 text-left text-sm font-medium"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {t('settings:teamManagement.journal.title')}
        <span className="text-xs text-muted-foreground">{open ? t('settings:teamManagement.journal.hide') : t('settings:teamManagement.journal.show')}</span>
      </button>
      {open && (
        <div className="border-t px-4 py-3 text-sm">
          {journal.isLoading ? (
            <p className="text-muted-foreground">{t('settings:teamManagement.journal.loading')}</p>
          ) : journal.isError ? (
            <div className="flex items-center gap-3">
              <p>{t('settings:teamManagement.journal.failed')}</p>
              <Button size="sm" variant="outline" onClick={() => void journal.refetch()}>{t('common:actions.retry')}</Button>
            </div>
          ) : (journal.data?.items.length ?? 0) === 0 ? (
            <p className="text-muted-foreground">{t('settings:teamManagement.journal.empty')}</p>
          ) : (
            <ul className="divide-y" data-testid="team-role-journal">
              {journal.data!.items.map((e, i) => (
                <li key={`${e.created_date}-${i}`} className="py-2">
                  <span>
                    {t(`settings:teamManagement.journal.actions.${e.action.replace('membership.', '')}`, {
                      who: e.actor_email || '?', member: e.member_email || '?', from: role(e.previous_role), to: role(e.new_role),
                    })}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">{new Date(e.created_date).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function MemberRow({
  m,
  organisationId,
  customers,
  canEdit,
  isSelf = false,
  isLastAdmin = false,
}: {
  m: any;
  organisationId: string;
  customers: Customer[];
  canEdit: boolean;
  /** The row of the signed-in member's own membership. */
  isSelf?: boolean;
  /** The organisation's only administrator: cannot be demoted. */
  isLastAdmin?: boolean;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const ROLE_LABELS = roleLabels(t);
  const changeRole = useChangeMemberRole(organisationId);
  const removeMember = useRemoveMember(organisationId);
  const [ask, confirmDialog] = useConfirmDialog();
  const [pendingRole, setPendingRole] = useState<Role | null>(null);

  return (
    <TableRow>
      <TableCell className="font-medium">{m.full_name || m.username}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{m.email}</TableCell>
      <TableCell>
        {canEdit ? (
          <>
          <Select
            value={pendingRole ?? m.role}
            onValueChange={async (v) => {
              // DES-13: reversible, so no confirmation; « Annuler » puts the previous role back.
              const previous = m.role as Role;
              const next = v as Role;
              if (next === previous) return;
              // REV-W4-F9: demoting oneself takes away the right to undo it
              // (the undo answered 403), so it is not reversible: confirm
              // first, and no « Annuler » afterwards (DES-13).
              const selfDemotion = isSelf && previous === 'tenant_admin' && next !== 'tenant_admin';
              if (selfDemotion && !(await ask({
                title: t('settings:teamManagement.members.selfDemote.title'),
                description: t('settings:teamManagement.members.selfDemote.body', { role: ROLE_LABELS[next] }),
                confirmLabel: t('settings:teamManagement.members.selfDemote.confirm'),
              }))) return;
              setPendingRole(next);
              try {
                await changeRole.mutateAsync({ membershipId: m.membership_id, role: next });
                if (selfDemotion) {
                  toast.success(t('settings:teamManagement.members.roleChanged', { email: m.email, role: ROLE_LABELS[next] }));
                  return;
                }
                toast.success(t('settings:teamManagement.members.roleChanged', { email: m.email, role: ROLE_LABELS[next] }), {
                  action: {
                    label: t('common:actions.cancel'),
                    onClick: () => {
                      changeRole.mutateAsync({ membershipId: m.membership_id, role: previous })
                        .then(() => toast.success(t('settings:teamManagement.members.roleRestored', { email: m.email, role: ROLE_LABELS[previous] })))
                        .catch((e) => showErrorToast(e));
                    },
                  },
                });
              } catch (e) {
                showErrorToast(e);
              } finally {
                setPendingRole(null);
              }
            }}
          >
            <SelectTrigger className="w-[140px] h-8" aria-label={t('settings:teamManagement.members.roleAria', { email: m.email })}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INVITABLE_ROLES.map((r) => (
                // The last administrator keeps the role: nobody could give it back.
                <SelectItem key={r} value={r} disabled={isLastAdmin && r !== 'tenant_admin'}>{ROLE_LABELS[r]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLastAdmin && <p className="mt-1 text-[11px] text-muted-foreground">{t('settings:teamManagement.members.lastAdmin')}</p>}
          </>
        ) : (
          <Badge variant={ROLE_VARIANTS[m.role as Role] ?? 'outline'}>
            {ROLE_LABELS[m.role as Role] ?? m.role}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <CustomerScopeCell
          membershipId={m.membership_id}
          role={m.role as Role}
          selected={Array.isArray(m.customer_ids) ? m.customer_ids : []}
          customers={customers}
          organisationId={organisationId}
          canEdit={canEdit}
        />
      </TableCell>
      <TableCell>
        <Badge variant={m.is_active ? 'default' : 'outline'}>
          {m.is_active ? t('settings:teamManagement.members.active') : t('settings:teamManagement.members.inactive')}
        </Badge>
      </TableCell>
      {canEdit && (
        <TableCell className="text-right">
          {confirmDialog}
          {/* QA-W6-8: the server refuses to remove the last administrator
              (LAST_ADMIN_CANNOT_LEAVE): the button says why instead. The
              wrapper carries the tooltip, a disabled button shows none. */}
          <span className="inline-flex" title={isLastAdmin ? t('settings:teamManagement.members.lastAdminRemove') : undefined}>
          <Button
            size="icon"
            variant="ghost"
            disabled={isLastAdmin}
            aria-label={t('settings:teamManagement.members.removeAria', { email: m.email })}
            aria-description={isLastAdmin ? t('settings:teamManagement.members.lastAdminRemove') : undefined}
            title={isLastAdmin ? undefined : t('settings:teamManagement.members.removeAria', { email: m.email })}
            onClick={async () => {
              if (!(await ask({ title: t('settings:teamManagement.members.removeConfirm', { email: m.email }), confirmLabel: t('common:actions.delete') }))) return;
              try {
                await removeMember.mutateAsync(m.membership_id);
              } catch (e) { showErrorToast(e); }
            }}
          >
            <Trash2 className="size-4 text-destructive" />
          </Button>
          </span>
        </TableCell>
      )}
    </TableRow>
  );
}

// ADR-027 / ADR-024 — the customers a member works for, as pills (W4,
// B2-team: the table answers « who calls for whom »). An admin reaches every
// customer. A viewer with no customer sees all of them (legacy default); a
// caller with none has nothing to call (« Aucun client assigné »).
const MAX_PILLS = 3;

export function CustomerPills({ role, selected, customers }: { role: Role; selected: string[]; customers: Customer[] }) {
  const { t } = useTranslation(['settings']);
  if (role === 'tenant_admin') return <span className="text-xs text-muted-foreground">{t('settings:teamManagement.scopePicker.allAdmin')}</span>;
  if (selected.length === 0) {
    return role === 'viewer'
      ? <span className="text-xs text-muted-foreground">{t('settings:teamManagement.scopePicker.all')}</span>
      : <span className="text-xs font-medium" style={{ color: 'var(--vf-status-callback-text, var(--vf-orange))' }}>{t('settings:teamManagement.scopePicker.noneAssigned')}</span>;
  }
  const names = selected.map((id) => customers.find((c) => c.id === id)?.company_name || t('settings:teamManagement.scopePicker.oneCustomer'));
  return (
    <span className="flex flex-wrap gap-1" data-testid="customer-pills">
      {names.slice(0, MAX_PILLS).map((n, i) => (
        <span key={`${n}-${i}`} className="inline-flex max-w-[140px] items-center truncate rounded-full border px-2 py-0.5 text-[11px] font-medium" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-2)' }} title={n}>{n}</span>
      ))}
      {names.length > MAX_PILLS && (
        <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] text-muted-foreground" title={names.slice(MAX_PILLS).join(', ')}>
          {t('settings:teamManagement.scopePicker.more', { count: names.length - MAX_PILLS })}
        </span>
      )}
    </span>
  );
}

function CustomerScopeCell({
  membershipId,
  role,
  selected,
  customers,
  organisationId,
  canEdit,
}: {
  membershipId: string;
  role: Role;
  selected: string[];
  customers: Customer[];
  organisationId: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const setIds = useSetMemberCustomerIds(organisationId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);

  const pills = <CustomerPills role={role} selected={selected} customers={customers} />;
  if (!canEdit || role === 'tenant_admin') return pills;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setDraft(selected);
      }}
    >
      <div className="flex items-center gap-1">
        {pills}
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label={t('settings:teamManagement.scopePicker.editAria')}>
            <Pencil className="size-3.5" aria-hidden />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-72 p-2 space-y-2" align="start">
        <p className="text-xs text-muted-foreground px-1">
          {t(role === 'viewer' ? 'settings:teamManagement.scopePicker.emptyMeansAll' : 'settings:teamManagement.scopePicker.emptyMeansNone')}
        </p>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {customers.length === 0 ? (
            <p className="text-xs text-muted-foreground px-1 py-2">{t('settings:teamManagement.scopePicker.noCustomers')}</p>
          ) : (
            customers.map((c) => {
              const checked = draft.includes(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-2 text-sm px-1 py-1 rounded hover:bg-accent cursor-pointer"
                >
                  {/* Checkbox is a JS @ts-nocheck forwardRef — cast to any
                      to expose its real Radix props (checked, onCheckedChange). */}
                  {(() => {
                    const C = Checkbox as unknown as (props: {
                      checked: boolean;
                      onCheckedChange: (v: boolean) => void;
                    }) => JSX.Element;
                    return (
                      <C
                        checked={checked}
                        onCheckedChange={(v: boolean) => {
                          setDraft((prev) =>
                            v ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                          );
                        }}
                      />
                    );
                  })()}
                  <span className="truncate">{c.company_name || c.email}</span>
                </label>
              );
            })
          )}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            size="sm"
            disabled={setIds.isPending}
            onClick={async () => {
              try {
                await setIds.mutateAsync({ membershipId, customerIds: draft });
                setOpen(false);
              } catch (e) {
                showErrorToast(e);
              }
            }}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function InviteMemberDialog({ organisationId, onClose }: { organisationId: string; onClose: () => void }) {
  const { t } = useTranslation(['settings', 'common']);
  const ROLE_LABELS = roleLabels(t);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('caller');
  const invite = useInviteMember(organisationId);

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('settings:teamManagement.members.invite')}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="space-y-2">
          <Label>{t('settings:teamManagement.members.columns.email')}</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemple.com" />
        </div>
        <div className="space-y-2">
          <Label>{t('settings:teamManagement.inviteDialog.fullName')}</Label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <p className="text-xs text-muted-foreground">{t('settings:teamManagement.inviteDialog.newAccountHint')}</p>
        </div>
        <div className="space-y-2">
          <Label>{t('settings:teamManagement.members.columns.role')}</Label>
          <Select value={role} onValueChange={(v) => setRole(v as Role)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {INVITABLE_ROLES.map((r) => (
                <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common:actions.cancel')}</Button>
        <Button
          onClick={async () => {
            try {
              const created = await invite.mutateAsync({ email, role, full_name: fullName || undefined });
              if (created) toast.success(t('settings:teamManagement.inviteDialog.created', { email }));
              onClose();
            } catch (e) { showErrorToast(e); }
          }}
          disabled={!email || invite.isPending}
        >
          {invite.isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
          {t('settings:teamManagement.inviteDialog.submit')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ─── Customers section (ADR-024) ─────────────────────────────────────────

function CustomersSection({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation(['settings', 'common']);
  const [ask, confirmDialog] = useConfirmDialog();
  const qc = useQueryClient();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin) ?? false;
  // ADR-031 — a non-admin member sees ONLY the Customers explicitly assigned
  // to their Membership (callerData.customer_ids). Empty list means "all
  // tenant customers" for legacy memberships without explicit scope.
  const myCustomerIds = useAuthStore((s) => s.activeMembership?.customerIds) ?? [];
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [creating, setCreating] = useState(false);
  const colCount = canEdit ? 6 : 5;

  const { data: allCustomers = [], isLoading } = useQuery({
    queryKey: ['customers', { includeArchived }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: !includeArchived, signal }),
    staleTime: 60_000,
  });

  const customers = canEdit || myCustomerIds.length === 0
    ? allCustomers
    : allCustomers.filter((c) => myCustomerIds.includes(c.id));

  const archive = useMutation({
    mutationFn: (id: string) => archiveCustomer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => reactivateCustomer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });

  // ADR-024 phase 2.b — one-shot migration helper for tenants who already
  // had data before customer-scoping was introduced. Creates a homonymous
  // Customer + reassigns every legacy prospect/call/meeting to it.
  const migrate = useMutation({
    mutationFn: () => migrateFromTenant(),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['prospect-queue'] });
      qc.invalidateQueries({ queryKey: ['parallel-calls'] });
      const { prospects, calls, meetings } = result.migrated;
      toast.success(t('settings:teamManagement.migration.migrateSuccess', { prospects, calls, meetings, name: result.customer.company_name }));
    },
    onError: (e) => showErrorToast(e),
  });

  // ADR-025 — super_admin maintenance op. Walks every tenant-scoped
  // collection and copies organisation_id from client_email so the future
  // ADR-026 scoping switch can read organisation_id directly. Idempotent.
  const backfill = useMutation({
    mutationFn: () => backfillOrganisationId(),
    onSuccess: (result) => {
      const counts = Object.entries(result.backfilled).map(([k, v]) => `${k}: ${v}`).join(' · ');
      toast.success(t('settings:teamManagement.migration.backfillSuccess', { count: result.organisationCount, counts }));
    },
    onError: (e) => showErrorToast(e),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">{t('settings:teamManagement.customers.count', { count: customers.length })}</p>
          <label className="text-xs flex items-center gap-1.5 text-muted-foreground">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t('settings:teamManagement.migration.includeArchived')}
          </label>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {confirmDialog}
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!(await ask({ title: t('settings:teamManagement.migration.migrateConfirm'), confirmLabel: t('common:actions.confirm'), tone: 'default' }))) return;
                migrate.mutate();
              }}
              disabled={migrate.isPending}
              title={t('settings:teamManagement.migration.migrateTooltip')}
            >
              {migrate.isPending && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
              {t('settings:teamManagement.migration.migrateButton')}
            </Button>
            {/* ADR-025 super-admin only — opération globale qui balaye toute la BDD. */}
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!(await ask({ title: t('settings:teamManagement.migration.backfillConfirm'), confirmLabel: t('common:actions.confirm'), tone: 'default' }))) return;
                  backfill.mutate();
                }}
                disabled={backfill.isPending}
                title={t('settings:teamManagement.migration.backfillTooltip')}
              >
                {backfill.isPending && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
                {t('settings:teamManagement.migration.backfillButton')}
              </Button>
            )}
            <Dialog open={creating} onOpenChange={setCreating}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5">
                  <Plus className="size-4" /> {t('settings:teamManagement.customers.newCustomer')}
                </Button>
              </DialogTrigger>
              <CustomerFormDialog onClose={() => setCreating(false)} />
            </Dialog>
          </div>
        )}
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('settings:teamManagement.customers.columns.company')}</TableHead>
              <TableHead>{t('settings:teamManagement.customers.columns.contact')}</TableHead>
              <TableHead>{t('settings:teamManagement.members.columns.email')}</TableHead>
              <TableHead>{t('settings:teamManagement.customers.columns.phone')}</TableHead>
              <TableHead className="w-[100px]">{t('settings:teamManagement.members.columns.status')}</TableHead>
              {canEdit && <TableHead className="w-[110px] text-right">{t('settings:teamManagement.members.columns.actions')}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={colCount} className="text-center py-8">
                <Loader2 className="size-5 animate-spin mx-auto" />
              </TableCell></TableRow>
            ) : customers.length === 0 ? (
              <TableRow><TableCell colSpan={colCount} className="text-center py-8 text-muted-foreground text-sm">
                {t('settings:teamManagement.customers.empty')}{canEdit ? ` ${t('settings:teamManagement.customers.emptyCreateFirst')}` : ''}
              </TableCell></TableRow>
            ) : (
              customers.map((c) => (
                <TableRow key={c.id} className={c.is_active ? '' : 'opacity-60'}>
                  <TableCell className="font-medium">{c.company_name}</TableCell>
                  <TableCell className="text-sm">{c.contact_name || '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.email || '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.phone || '—'}</TableCell>
                  <TableCell>
                    <Badge variant={c.is_active ? 'default' : 'outline'}>
                      {c.is_active ? t('settings:teamManagement.members.active') : t('settings:teamManagement.customers.archived')}
                    </Badge>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(c)}>
                        <Pencil className="size-4" />
                      </Button>
                      {c.is_active ? (
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => archive.mutate(c.id)}
                          disabled={archive.isPending}
                        >
                          <Archive className="size-4 text-destructive" />
                        </Button>
                      ) : (
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => reactivate.mutate(c.id)}
                          disabled={reactivate.isPending}
                        >
                          <RotateCcw className="size-4 text-green-600" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <CustomerFormDialog customer={editing} onClose={() => setEditing(null)} />
        </Dialog>
      )}
    </div>
  );
}

export function CustomerFormDialog({ customer, onClose }: { customer?: Customer; onClose: () => void }) {
  const { t } = useTranslation(['settings', 'common']);
  const qc = useQueryClient();
  const isEdit = !!customer;
  const [form, setForm] = useState({
    company_name: customer?.company_name ?? '',
    contact_name: customer?.contact_name ?? '',
    email: customer?.email ?? '',
    phone: customer?.phone ?? '',
    industry: customer?.industry ?? '',
  });
  // DUP-1: the meeting link is edited in Paramètres › Intégrations only; shown here, never sent.
  const calendlyUrl = customer?.calendly_url ?? '';

  const save = useMutation({
    mutationFn: () => isEdit
      ? updateCustomer(customer!.id, form)
      : createCustomer(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
    onError: (e) => showErrorToast(e),
  });

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{isEdit ? t('settings:teamManagement.customerForm.editTitle') : t('settings:teamManagement.customers.newCustomer')}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 py-2">
        <Field label={t('settings:teamManagement.customerForm.companyLabel')} value={form.company_name} onChange={(v) => setForm((f) => ({ ...f, company_name: v }))} />
        <Field label={t('settings:teamManagement.customers.columns.contact')} value={form.contact_name} onChange={(v) => setForm((f) => ({ ...f, contact_name: v }))} />
        <Field label={t('settings:teamManagement.members.columns.email')} value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} type="email" />
        <Field label={t('settings:teamManagement.customers.columns.phone')} value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
        <Field label={t('settings:teamManagement.customerForm.industryLabel')} value={form.industry} onChange={(v) => setForm((f) => ({ ...f, industry: v }))} />

        {/* ADR-024 phase 2.c — per-Customer CRM. Each Customer has its own
            HubSpot account / Google Calendar / Calendly URL so a tenant
            that prospects for several B2B clients can route each one's
            data to the right CRM. */}
        <div className="border-t pt-3 mt-2 space-y-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">{t('settings:teamManagement.customerForm.crmSectionTitle')}</p>
          <div className="space-y-1 text-sm">
            <p className="font-medium">{t('settings:teamManagement.customerForm.calendlyLabel')}</p>
            <p className="break-all text-muted-foreground">{calendlyUrl || t('settings:teamManagement.customerForm.calendlyNone')}</p>
            <p className="text-xs text-muted-foreground">{t('settings:teamManagement.customerForm.calendlyWhere')}</p>
          </div>

          {customer && (
            <>
              {/* HubSpot hidden from the UI (AUDIT-INT-08, src/lib/hubspotUi.ts). */}
              {HUBSPOT_UI_ENABLED && (
                <CustomerCrmConnector
                  customer={customer}
                  provider="hubspot"
                  connected={customer.hubspot_connected}
                  authorizeUrl={customerHubspotAuthorizeUrl(customer.id)}
                  disconnect={() => disconnectCustomerHubspot(customer.id)}
                />
              )}
              <CustomerCrmConnector
                customer={customer}
                provider="google"
                connected={customer.google_calendar_enabled}
                accountEmail={customer.google_calendar_account_email}
                authorizeUrl={customerGoogleAuthorizeUrl(customer.id)}
                disconnect={() => disconnectCustomerGoogle(customer.id)}
              />
            </>
          )}
          {!customer && (
            <p className="text-[10px] text-muted-foreground italic">
              {t(HUBSPOT_UI_ENABLED
                ? 'settings:teamManagement.customerForm.saveFirstHint'
                : 'settings:teamManagement.customerForm.saveFirstHintNoHubspot')}
            </p>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common:actions.cancel')}</Button>
        <Button
          onClick={() => save.mutate()}
          disabled={!form.company_name.trim() || save.isPending}
        >
          {save.isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
          {isEdit ? t('common:actions.save') : t('settings:teamManagement.customerForm.create')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CustomerCrmConnector({
  customer, provider, connected, accountEmail, authorizeUrl, disconnect,
}: {
  customer: Customer;
  provider: 'hubspot' | 'google';
  connected: boolean;
  accountEmail?: string;
  authorizeUrl: string;
  disconnect: () => Promise<void>;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const qc = useQueryClient();
  const label = provider === 'hubspot' ? 'HubSpot CRM' : 'Google Calendar';
  const [ask, confirmDialog] = useConfirmDialog();
  const handleConnect = () => {
    // Full-page redirect — the browser hits the API which 302-redirects
    // to the provider's consent page.
    window.location.href = authorizeUrl;
  };
  const handleDisconnect = async () => {
    if (!(await ask({ title: t('settings:teamManagement.crmConnector.disconnectConfirm', { label, company: customer.company_name }), confirmLabel: t('settings:teamManagement.crmConnector.disconnect') }))) return;
    try {
      await disconnect();
      qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success(t('settings:teamManagement.crmConnector.disconnected', { label }));
    } catch (e) { showErrorToast(e); }
  };
  return (
    <div className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 bg-muted/30">
      {confirmDialog}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium">{label}</p>
        {connected ? (
          <p className="text-[10px] text-green-700 truncate">
            {t('settings:teamManagement.crmConnector.connected')}{accountEmail ? ` — ${accountEmail}` : ''}
          </p>
        ) : (
          <p className="text-[10px] text-muted-foreground">{t('settings:teamManagement.crmConnector.notConnected')}</p>
        )}
      </div>
      {connected ? (
        <Button size="sm" variant="outline" onClick={handleDisconnect}>{t('settings:teamManagement.crmConnector.disconnect')}</Button>
      ) : (
        <Button size="sm" onClick={handleConnect}>{t('settings:teamManagement.crmConnector.connect')}</Button>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; type?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// ─── Assignments section: Caller ↔ Customers (ADR-024) ───────────────────

function AssignmentsSection({ organisationId, canEdit }: { organisationId: string; canEdit: boolean }) {
  const { t } = useTranslation(['settings', 'common']);
  const { data: members = [] } = useMembers(organisationId);
  const { data: customers = [] } = useQuery({
    queryKey: ['customers', { includeArchived: false }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: true, signal }),
    staleTime: 60_000,
  });

  const callerOnlyMembers = members.filter((m: any) => m.role === 'caller');

  // ADR-031 — caller→customer assignment is now stored on the Membership
  // (member.customer_ids), mutated via PATCH /organisations/:id/members/:mid.
  const updateAssignment = useSetMemberCustomerIds(organisationId);

  if (customers.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          {canEdit
            ? t('settings:teamManagement.assignments.noCustomersEdit')
            : t('settings:teamManagement.assignments.noCustomersView')}
        </AlertDescription>
      </Alert>
    );
  }

  if (callerOnlyMembers.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          {canEdit
            ? t('settings:teamManagement.assignments.noMembersEdit')
            : t('settings:teamManagement.assignments.noMembersView')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {canEdit
          ? t('settings:teamManagement.assignments.helpEdit')
          : t('settings:teamManagement.assignments.helpView')}
      </p>

      <div className="space-y-3">
        {callerOnlyMembers.map((m: any) => {
          const assignedIds = new Set<string>(m.customer_ids ?? []);

          return (
            <div key={m.membership_id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{m.full_name || m.username}</p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </div>
                <Badge variant="secondary">{t('settings:teamManagement.assignments.customerCount', { count: assignedIds.size })}</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {customers.map((c) => {
                  const checked = assignedIds.has(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2 text-sm rounded border px-2 py-1.5 ${
                        canEdit ? 'cursor-pointer hover:bg-accent/40' : 'cursor-default opacity-90'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canEdit || updateAssignment.isPending}
                        onChange={(e) => {
                          if (!canEdit) return;
                          const next = new Set(assignedIds);
                          if (e.target.checked) next.add(c.id); else next.delete(c.id);
                          updateAssignment.mutate({ membershipId: m.membership_id, customerIds: Array.from(next) });
                        }}
                      />
                      <span className="truncate">{c.company_name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
